import SideNav from "../components/sideNav";
import BottomNav from "../components/bottomNav";
import { useHistory } from "react-router-dom";
import createPersistedState from "use-persisted-state";
import React, { useEffect, useState } from "react";
import { ReactComponent as LocIcon } from "../img/icons/icon_blue_location.svg";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { useRollbarPerson } from "@rollbar/react";
import {
  convertDateFormat,
  getPerson,
  getTranslatedClinicNameFromId,
} from "../utils";
import {
  Card,
  CardActions,
  CardContent,
  Button,
  makeStyles,
} from "@material-ui/core";
import { Link } from "react-router-dom/cjs/react-router-dom";

import { ReactComponent as ChatDoctorIcon } from "../img/icons/icon_chat_doctor.svg";
import { ReactComponent as PatientWaitingQueueIcon } from "../img/patientwating.svg";
import HealthReminder from "./healthinfo/HealthReminder";
import useHealthReminder from "../hooks/useHealthReminder";
import AssessmentReminderPopup from "../components/assessmentReminderPopup";
import { getToken } from "firebase/messaging";
import { messaging } from "../firebase";

const useTokenState = createPersistedState("token");
const usePIDState = createPersistedState("pid");
const useProfileState = createPersistedState("profile");
const useClinicState = createPersistedState("clinic");
const useIsChatButtonEnabledState = createPersistedState("isChatButtonEnabled");

const useStyles = makeStyles((theme) => ({
  button: {
    color: "#fd7e14",
    borderColor: "#fd7e14",
  },
  cardContent: {
    paddingRight: 10,
    paddingLeft: 10,
    paddingTop: 16,
    paddingBottom: 16,
  },
}));

function checkIfDateIsToday(date) {
  if (!date) return false;

  const targetDate = new Date(date); // Assuming this is in the format 'YYYY-MM-DD'

  const today = new Date();

  return targetDate.toDateString() === today.toDateString();
}

function HomePage(props) {
  useRollbarPerson(getPerson());
  const classes = useStyles();
  const history = useHistory();
  const [token, _] = useTokenState();
  const [pid, __] = usePIDState();
  const [profile, ___] = useProfileState();
  const [isChatButtonEnabled, setIsChatButtonEnabled] =
    useIsChatButtonEnabledState();
  const [isHealthInfoAvailable, setHealthInfoAvailability] = useState(false);
  const [isFetchingHealthInfo, setFetchingHealthInfoPhase] = useState(true);
  const [isFetchingMyAppointments, setFetchingMyAppointmentsPhase] =
    useState(true);
  const [currentAppointment, setCurrentAppointment] = useState(undefined);
  const [clinicId, setClinic] = useClinicState();

  const subNoteStyle = { marginTop: "3em", paddingBottom: "3em" };
  const getHealthInfoUrl = (pid) => `/api/v1/users/profile/${pid}/healthinfo`;
  const getChatAvailiblityStatusUrl = () => `/api/v1/chat/channel/status`;
  const getMyAppointmentsUrl = (pid) =>
    `/api/v1/appointments?attendeeId=${pid}`;
  const getNotificationUrl = (pid) => `/api/v1/users/notification/${pid}`;
  const getQueueStatusURL = (appointmentId) =>
    `/api/v1/appointments/${appointmentId}/queue/status`;
  const getQueueInfoURL = (appointmentId) =>
    `/api/v1/appointments/${appointmentId}/queue`;
  const { t, i18n } = useTranslation();
  const [reportsNotification, setReportsNotification] = useState([]);
  const [prescriptionsNotification, setPrescriptionsNotification] = useState(
    []
  );
  const [dietNotification, setDietNotification] = useState([]);
  const [basdaiNotification, setBasdaiNotification] = useState([]);
  const [basfiNotification, setBasfiNotification] = useState([]);
  const [rapid3Notification, setRapid3Notification] = useState([]);

  const [nextApp, setNextApp] = useState(undefined);
  const [isAppointmentQueueEnabled, setIsAppointmentQueueEnabled] = useState();
  const [appointmentQueueDetails, setAppointmentQueueDetails] = useState();
  const WAITING_QUEUE_POLL_INTERVAL = 30000;

  const { showHealthInfoReminder, dismissHealthInfoReminder } =
    useHealthReminder(profile?.healthInfoPopup);

  const [showBASDAIReminderPopup, setShowBASDAIReminderPopup] = useState(
    profile?.basdaiPopup || false
  );
  const [showBASFIReminderPopup, setShowBASFIReminderPopup] = useState(
    profile?.basfiPopup || false
  );
  const [showRapid3ReminderPopup, setShowRapid3ReminderPopup] = useState(
    profile?.rapid3Popup || false
  );

  useEffect(() => {
    (async () => {
      if (pid && token) {
        fetchHealthInfo(pid);
        const appointmentInfo = await fetchAppointmentInfo(pid);
        if (appointmentInfo && checkIfDateIsToday(appointmentInfo.date)) {
          const waitingQueueStatusApiResponse =
            await fetchWaitQueueAvailiblityStatus(appointmentInfo.id);
        }

        // TODO: Fetch waitlist info as well
        fetchNotificationsInfo(pid);
        fetchPx(pid);
        fetchChatAvailiblityStatus();
      }
    })();

    // IMPORTANT NOTE:
    // Commenting the following section
    // Based on observations made in clinic
    // seems like the if clause above can evaluate
    // to FALSE even after successful calls are made, which
    // results in a logout if the following else condition
    // is executed.
    // The rationale to comment for now is, the fetch calls
    // above even if not made wouldn't break the app and hence
    // for now let us not force a logout and see the behaviour in
    // production
    //
    //else {
    //  history.push("/login");
    //}
  }, [pid, token]);

  useEffect(() => {
    let waitingQueueIntervalReference;
    (() => {
      if (isAppointmentQueueEnabled) {
        fetchWaitQueueInfo(currentAppointment.id);
        waitingQueueIntervalReference = setInterval(
          () => fetchWaitQueueInfo(currentAppointment.id),
          WAITING_QUEUE_POLL_INTERVAL
        );
      }
    })();

    return () => {
      if (waitingQueueIntervalReference) {
        clearInterval(waitingQueueIntervalReference);
      }
    };
  }, [pid, token, isAppointmentQueueEnabled]);

  useEffect(() => {}, [reportsNotification, prescriptionsNotification]);

  const fetchAppointmentInfo = (pid) => {
    return fetch(getMyAppointmentsUrl(pid), {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + token,
      },
    })
      .then((response) => response.json())
      .then((responseData) => {
        if (responseData["status"]["success"]) {
          setCurrentAppointment(responseData["appointment"]);
          return responseData["appointment"];
        } else if (responseData["status"]["code"] === 4011) {
          localStorage.clear();
          history.push("/login");
        }
      })
      .catch(function (error) {
        console.log(error.message); //TODO: Report error
        //history.push("/login");
      })
      .finally(() => {
        setFetchingMyAppointmentsPhase(false);
      });
  };

  function hasUpcomingAppointmentToday() {
    if (
      currentAppointment &&
      currentAppointment.hasOwnProperty("date") &&
      currentAppointment.hasOwnProperty("appointmentStatus") &&
      currentAppointment.hasOwnProperty("startTime")
    ) {
      const appointmentDateStr = moment(
        new Date(currentAppointment["date"])
      ).format("YYYY-MM-DD");
      const todayStr = moment().format("YYYY-MM-DD");
      const currentDate = new Date();
      const appDate = fetchDatetimeObj(
        currentAppointment["date"],
        currentAppointment["startTime"]
      );
      return (
        todayStr === appointmentDateStr &&
        (currentAppointment["appointmentStatus"] === "Rescheduled" ||
          currentAppointment["appointmentStatus"] === "Scheduled") &&
        appDate > currentDate
      );
    } else {
      return false;
    }
  }
  function hasAppointmentToday() {
    if (
      currentAppointment &&
      currentAppointment.hasOwnProperty("date") &&
      currentAppointment.hasOwnProperty("appointmentStatus") &&
      currentAppointment.hasOwnProperty("startTime")
    ) {
      const appointmentDateStr = moment(
        new Date(currentAppointment["date"])
      ).format("YYYY-MM-DD");
      const todayStr = moment().format("YYYY-MM-DD");
      const currentDate = new Date();
      const appDate = fetchDatetimeObj(
        currentAppointment["date"],
        currentAppointment["startTime"]
      );
      return (
        todayStr === appointmentDateStr &&
        (currentAppointment["appointmentStatus"] === "Rescheduled" ||
          currentAppointment["appointmentStatus"] === "Scheduled")
      );
    } else {
      return false;
    }
  }

  function fetchDatetimeObj(dateStr, timeStr) {
    try {
      var date = new Date(dateStr);

      const hours = timeStr?.split(":")[0];
      const minutes = timeStr?.split(":")[1]?.split(" ")[0];
      const period = timeStr?.split(" ")[1]?.toLowerCase();

      if (period === "pm" && hours !== "12") {
        date.setHours(parseInt(hours) + 12);
      } else if (period === "am" && hours === "12") {
        date.setHours(0);
      } else {
        date.setHours(parseInt(hours));
      }

      date.setMinutes(minutes);

      return date;
    } catch (e) {}
  }

  const fetchWaitQueueInfo = (appointmentId) => {
    fetch(getQueueInfoURL(appointmentId), {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + token,
      },
    })
      .then((response) => response.json())
      .then((responseData) => {
        if (responseData["status"]["success"]) {
          setAppointmentQueueDetails({ ...responseData });
        } else if (responseData["status"]["code"] === 4011) {
          localStorage.clear();
          history.push("/login");
        } else if (responseData["status"]["code"] === 400) {
          setAppointmentQueueDetails(undefined);
          setIsAppointmentQueueEnabled(false);
        } else {
        }
      })
      .catch(function (error) {})
      .finally(() => {});
  };

  const fetchWaitQueueAvailiblityStatus = (appointmentId) => {
    return fetch(getQueueStatusURL(appointmentId), {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + token,
      },
    })
      .then((response) => response.json())
      .then((responseData) => {
        if (
          responseData["status"]["success"] &&
          responseData["enable"] === true
        ) {
          setIsAppointmentQueueEnabled(true);
          return true;
        } else if (responseData["status"]["code"] === 4011) {
          localStorage.clear();
          history.push("/login");
        } else {
          setIsAppointmentQueueEnabled(false);
          return false;
        }
      })
      .catch(function (error) {
        setIsAppointmentQueueEnabled(false);
      })
      .finally(() => {});
  };

  const fetchChatAvailiblityStatus = () => {
    fetch(getChatAvailiblityStatusUrl(), {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + token,
      },
    })
      .then((response) => response.json())
      .then((responseData) => {
        if (
          responseData["status"]["success"] &&
          responseData["disabled"] === false
        ) {
          setIsChatButtonEnabled(true);
        } else if (responseData["status"]["code"] === 4011) {
          localStorage.clear();
          history.push("/login");
        } else {
          setIsChatButtonEnabled(false);
        }
      })
      .catch(function (error) {
        setIsChatButtonEnabled(false);
        console.log(error.message); //TODO: Report error
        //localStorage.clear();
        //history.push("/login");
      })
      .finally(() => {});
  };

  const fetchHealthInfo = (pid) => {
    fetch(getHealthInfoUrl(pid), {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + token,
      },
    })
      .then((response) => response.json())
      .then((responseData) => {
        if (
          responseData["status"]["success"] &&
          responseData["healthInfo"] &&
          responseData["healthInfo"][0] &&
          responseData["healthInfo"][0]["response"] !== null
        ) {
          setHealthInfoAvailability(true);
        } else if (responseData["status"]["code"] === 4011) {
          localStorage.clear();
          history.push("/login");
        }
      })
      .catch(function (error) {
        console.log(error.message); //TODO: Report error
        //localStorage.clear();
        //history.push("/login");
      })
      .finally(() => setFetchingHealthInfoPhase(false));
  };

  const fetchNotificationsInfo = (pid) => {
    fetch(getNotificationUrl(pid), {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + token,
      },
    })
      .then((response) => response.json())
      .then((responseData) => {
        if (responseData["status"]["success"]) {
          responseData.notifications.map(function (notif, index) {
            if (notif.code === "100001") {
              let rep = reportsNotification;
              rep.push(responseData.notifications[index]);
              setReportsNotification(rep);
            } else if (notif.code === "100002") {
              let pres = prescriptionsNotification;
              pres.push(responseData.notifications[index]);
              setPrescriptionsNotification(pres);
            } else if (notif.code === "100003") {
              let dietItems = dietNotification;
              dietItems.push(responseData.notifications[index]);
              setDietNotification(dietItems);
            } else if (notif.code === "100004") {
              let basdaiItems = basdaiNotification;
              basdaiItems.push(responseData.notifications[index]);
              setBasdaiNotification(basdaiItems);
            } else if (notif.code === "100005") {
              let basfiItems = basfiNotification;
              basfiItems.push(responseData.notifications[index]);
              setBasfiNotification(basfiItems);
            } else if (notif.code === "100006") {
              let rapid3Items = rapid3Notification;
              rapid3Items.push(responseData.notifications[index]);
              setRapid3Notification(rapid3Items);
            }
          });
        } else if (responseData["status"]["code"] === 4011) {
          localStorage.clear();
          history.push("/login");
        }
      })
      .catch(function (error) {
        console.log("Failed to fetch reports notification");
      });
  };

  const markNotificationAsSeen = (notifId) => {
    fetch(getNotificationUrl(notifId), {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + token,
      },
    })
      .then((response) => response.json())
      .then((responseData) => {
        if (responseData["status"]["code"] === 4011) {
          localStorage.clear();
          history.push("/login");
        }
      })
      .catch(function (error) {
        console.log("Failed to fetch reports notification");
      });
  };

  const fetchPx = (pId) => {
    fetch(`/api/v1/users/prescriptions/${pId}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + token,
      },
    })
      .then((response) => response.json())
      .then((responseData) => {
        if (responseData["status"]["success"]) {
          // TODO: Add relevant validation here
          if (responseData["prescription"] != null)
            setNextApp(responseData["prescription"]?.followUpDate);
        }
        // TODO: Else report error
      })
      .catch((e) => {
        console.log(e.message); //TODO: Report error
      });
  };

  function HealthInfoStatusComponent({ isFD, isHIA }) {
    if (!isFD && isHIA) {
      return (
        <>
          <span className="icon_dot bg_green"></span>&nbsp;{" "}
          {t("todaySection.HOME.complete")}
        </>
      );
    } else if (!isFD && !isHIA) {
      return (
        <>
          <span className="icon_dot bg_red"></span>&nbsp;{" "}
          {t("todaySection.HOME.pending")}
        </>
      );
    } else {
      return <></>;
    }
  }

  function HealthInfoCTAComponent({ isFD, isHIA }) {
    if (!isFD && isHIA) {
      return (
        <button
          className="btn btn_primary"
          onClick={() => history.push("/health-info/start")}
        >
          {t("todaySection.HOME.update")}
        </button>
      );
    } else if (!isFD && !isHIA) {
      return (
        <button
          className="btn btn_primary"
          onClick={() => history.push("/health-info/start")}
        >
          {t("todaySection.HOME.start")}
        </button>
      );
    } else {
      return <></>;
    }
  }

  function MainTextComponent({ isFD, isHIA, isFMA }) {
    if (!isFD && !isFMA && isAppointmentQueueEnabled && hasAppointmentToday()) {
      return (
        <div style={{ marginBottom: "35px" }}>
          <QueueComponent
            appointmentDetails={currentAppointment}
            appointmentQueueDetails={appointmentQueueDetails}
          />
        </div>
      );
    } else if (!isFD && !isFMA && !hasUpcomingAppointmentToday()) {
      // Fetched health info but no appointments found

      if (!isHIA) {
        return (
          <>
            <h2 className="bigtitle mb-4 text-center">
              {t("todaySection.HOME.top-heading")} {profile?.firstName}!
            </h2>
            <h3 className="text-center subtitle">
              {t("todaySection.HOME.message1").split("--")[0]}
              <br />
              {t("todaySection.HOME.message1").split("--")[1]}
            </h3>
            <p className="text-center desc w-100">
              {t("todaySection.HOME.message2")}
            </p>
          </>
        );
      } else {
        return (
          <h2 className="bigtitle mb-4 text-center">
            {t("todaySection.HOME.top-heading")} {profile?.firstName}!
          </h2>
        );
      }
    } else if (!isFMA && hasUpcomingAppointmentToday()) {
      return <AppointmentTodayComponent />;
    } else if (!isFD && !isFMA) {
      return (
        <h2 className="bigtitle mb-4 text-center">
          {t("todaySection.HOME.top-heading")} {profile?.firstName}!
        </h2>
      );
    }
    return <></>;
  }

  function QueueComponent({ appointmentDetails, appointmentQueueDetails }) {
    const addThirtyMinutes = (timeString) => {
      // Split the timeString into hours, minutes, and AM/PM parts
      const [time, period] = timeString.split(" ");

      // Split the time into hours and minutes
      const [hours, minutes] = time.split(":").map(Number);

      // Convert hours to 24-hour format
      let adjustedHours = (hours % 12) + (period === "PM" ? 12 : 0);

      // Add 30 minutes
      let adjustedMinutes = minutes + 30;

      // Handle carry-over if minutes exceed 59
      if (adjustedMinutes >= 60) {
        adjustedHours++;
        adjustedMinutes -= 60;
      }

      // Convert back to 12-hour format
      const newPeriod = adjustedHours >= 12 ? "PM" : "AM";
      adjustedHours %= 12;
      adjustedHours = adjustedHours || 12; // Handle midnight (12 AM)

      // Format the new time string
      const newTimeString = `${adjustedHours
        .toString()
        .padStart(2, "0")}:${adjustedMinutes
        .toString()
        .padStart(2, "0")} ${newPeriod}`;

      return newTimeString;
    };

    console.log("appDetail", appointmentQueueDetails);

    if (appointmentQueueDetails?.doctorArrival == false)
      return (
        <>
          <div
            style={{
              display: "flex",
              marginBottom: "15px",
              justifyContent: "center",
              textAlign: "center",
              alignItems: "center",
              font: "normal normal normal 18px/27px Poppins",
            }}
          >
            Your session today
            <br />
            with {appointmentDetails.ownerMetadata.name} <br />
            between {appointmentDetails.startTime} and{" "}
            {addThirtyMinutes(appointmentDetails.startTime)}.
          </div>
          <div
            style={{ font: "normal normal medium 16px/25px Poppins" }}
            className="text-center mb-2"
          >
            <LocIcon style={{ marginRight: "10px" }} />
            {appointmentDetails.spaceMetadata.name}
          </div>
          <div
            style={{ font: "normal normal medium 16px/25px Poppins" }}
            className="text-center mb-4"
          >
            Shop no. 15-16, Mayuresh Srishti Complex, <br /> Rajiv Gandhi Nagar,
            Bhandup West, <br /> Mumbai, Maharashtra 400078
          </div>
        </>
      );
    else if (
      appointmentQueueDetails?.doctorArrival === true &&
      appointmentQueueDetails?.queueNumber === 1
    )
      return (
        <>
          <div
            style={{
              display: "flex",
              marginBottom: "10px",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <div
              style={{
                width: "95%",
                display: "flex",
                flexDirection: "column",
                background: " #FFD9BD 0% 0% no-repeat padding-box",
                border: " 1px solid #F9A466",
                borderRadius: "20px",
                justifyContent: "space-between",
                alignItems: "center",
                opacity: "1",
                paddingTop: "15px",
                paddingBottom: "15px",
                height: "230px",
                font: "normal normal medium 16px/25px Poppins",
              }}
            >
              The Doctor is going to see you next.
              <br /> Please wait near the Doctor's cabin.
              <PatientWaitingQueueIcon />
            </div>
          </div>
        </>
      );
    else if (
      appointmentQueueDetails?.doctorArrival == true &&
      appointmentQueueDetails?.queueNumber > 1
    )
      return (
        <>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <h2 className="subtitle mb-4 text-center">Your Queue Number</h2>
            <div
              style={{
                background: "#F9A466 0% 0% no-repeat padding-box",
                height: "50px",
                width: "50px",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                font: "normal normal bold 20px/30px Poppins",
              }}
              className="text-center mb-4"
            >
              {appointmentQueueDetails.queueNumber}
            </div>
            {appointmentQueueDetails.estimatedTime != "" && (
              <>
                <div
                  style={{
                    font: "font: normal normal medium 14px/21px Poppins",
                  }}
                  className="queuetext1 mb-2 text-center"
                >
                  Estimated Time of your appointment is{" "}
                  {appointmentQueueDetails.estimatedTime}
                </div>
                <div
                  style={{
                    font: "font: normal normal medium 14px/21px Poppins",
                  }}
                  className="queuetext2 text-center mb-4"
                >
                  *Times are updated in real-time.
                  <br /> Please check regularly
                </div>
              </>
            )}
            {appointmentDetails.attendeeStatus !== "Arrived" && (
              <div
                style={{ font: "font: normal normal medium 14px/21px Poppins" }}
                className="queuetext3 mb-2 text-center"
              >
                Please inform receptionist <br />
                upon arriving in clinic
              </div>
            )}
          </div>
        </>
      );
    else return <></>;
  }

  function AppointmentTodayComponent() {
    console.log(currentAppointment);

    return (
      <>
        <h2 className="subtitle mb-4 text-center">
          {i18n.language === "hi" || i18n.language === "mr" ? (
            <>
              <span>
                {t("todaySection.HOME.sessionTodayMsg1") +
                  moment(currentAppointment.slotId).format("A h:mm") +
                  t("todaySection.HOME.sessionTodayMsg3")}
              </span>
              <br />
              <span>
                {currentAppointment.ownerMetadata.name}{" "}
                {t("todaySection.HOME.sessionTodayMsg2")}
              </span>
            </>
          ) : (
            <>
              <span>
                {t("todaySection.HOME.sessionTodayMsg1") +
                  moment(currentAppointment.slotId).format("h:mm A")}
              </span>
              <br />
              <span>
                {t("todaySection.HOME.sessionTodayMsg2")}{" "}
                {currentAppointment.ownerMetadata.name}
              </span>
            </>
          )}
        </h2>
        <p className="txt_desc text-center w-100">
          <span className="icon mx-2">
            <LocIcon className="img-fluid" />
          </span>

          {getTranslatedClinicNameFromId(
            currentAppointment.spaceMetadata.code,
            t
          )}
        </p>
      </>
    );
  }

  function NotificationCard(details) {
    return (
      <Card elevation={0} variant="outlined" className="my-4">
        <CardContent>
          <div className="text-center w-500">{details.msg}</div>
        </CardContent>
        <CardActions>
          <Button
            variant="outlined"
            className={classes.button}
            style={{
              margin: "auto",
              display: "block",
              marginBottom: "10px",
            }}
            onClick={() => {
              details.notif.map((n) => {
                markNotificationAsSeen(n?.["id"]);
              });

              // Dismiss notification
              if (typeof details.onDismiss === "function") {
                details.onDismiss();
              }

              history.push(details.redirect);
            }}
          >
            {details.actionText}
          </Button>
        </CardActions>
      </Card>
    );
  }

  return (
    <>
      <SideNav />
      <HealthReminder
        show={showHealthInfoReminder}
        dismiss={dismissHealthInfoReminder}
      />
      {showBASDAIReminderPopup && (
        <AssessmentReminderPopup
          show={showBASDAIReminderPopup}
          dismiss={() => {
            setShowBASDAIReminderPopup(false);
          }}
          assessment="BASDAI"
        />
      )}

      {showBASFIReminderPopup && (
        <AssessmentReminderPopup
          show={showBASFIReminderPopup}
          dismiss={() => {
            setShowBASFIReminderPopup(false);
          }}
          assessment="BASFI"
        />
      )}

      {showRapid3ReminderPopup && (
        <AssessmentReminderPopup
          show={showRapid3ReminderPopup}
          dismiss={() => {
            setShowRapid3ReminderPopup(false);
          }}
          assessment="RAPID3"
        />
      )}

      <div className="body_content">
        <div className="inner_body py-5">
          {isFetchingHealthInfo || isFetchingMyAppointments ? (
            <div
              style={{
                margin: "auto",
                display: "flex",
                justifyContent: "center",
              }}
              id="loader"
            ></div>
          ) : (
            <>
              <MainTextComponent
                isFD={isFetchingHealthInfo}
                isHIA={isHealthInfoAvailable}
                isFMA={isFetchingMyAppointments}
              />

              {/* notifications */}
              {reportsNotification.length !== 0 ? (
                <NotificationCard
                  notif={reportsNotification}
                  msg="Your test reports are ready"
                  redirect="/my-reports"
                  actionText="View"
                  onDismiss={() => {
                    setReportsNotification([]);
                  }}
                ></NotificationCard>
              ) : (
                <></>
              )}
              {prescriptionsNotification.length !== 0 ? (
                <NotificationCard
                  notif={prescriptionsNotification}
                  msg="Doctor has sent you the prescription"
                  redirect="/prescriptions"
                  actionText="View"
                  onDismiss={() => {
                    setPrescriptionsNotification([]);
                  }}
                ></NotificationCard>
              ) : (
                <></>
              )}

              {dietNotification.length !== 0 ? (
                <NotificationCard
                  notif={dietNotification}
                  msg="You have recieved the diet chart"
                  redirect="/diet"
                  actionText="View"
                  onDismiss={() => {
                    setDietNotification([]);
                  }}
                ></NotificationCard>
              ) : (
                <></>
              )}

              {basdaiNotification.length !== 0 ? (
                <NotificationCard
                  notif={basdaiNotification}
                  msg={t("notifications.basdaiNotificationMsg")}
                  redirect="/"
                  actionText={t("notifications.common.ok")}
                  onDismiss={() => {
                    setBasdaiNotification([]);
                  }}
                ></NotificationCard>
              ) : (
                <></>
              )}

              {basfiNotification.length !== 0 ? (
                <NotificationCard
                  notif={basfiNotification}
                  msg={t("notifications.basfiNotificationMsg")}
                  redirect="/"
                  actionText={t("notifications.common.ok")}
                  onDismiss={() => {
                    setBasfiNotification([]);
                  }}
                ></NotificationCard>
              ) : (
                <></>
              )}

              {rapid3Notification.length !== 0 ? (
                <NotificationCard
                  notif={rapid3Notification}
                  msg={t("notifications.rapid3NotificationMsg")}
                  redirect="/"
                  actionText={t("notifications.common.ok")}
                  onDismiss={() => {
                    setRapid3Notification([]);
                  }}
                ></NotificationCard>
              ) : (
                <></>
              )}

              {nextApp != null &&
              nextApp !== "" &&
              currentAppointment === undefined ? (
                <Card elevation={0} variant="outlined" className="my-4">
                  <CardContent className={classes.cardContent}>
                    <div className="text-center w-500">
                      Recommended date for next appointment
                      <br />
                      {moment(new Date(nextApp)).format("DD MMMM YYYY")}
                    </div>
                  </CardContent>
                  <CardActions>
                    <Button
                      variant="outlined"
                      className={classes.button}
                      style={{
                        margin: "auto",
                        display: "block",
                        marginBottom: "10px",
                      }}
                      onClick={() => {
                        history.push({
                          pathname: "/schedule-appointment",
                          state: {
                            recDate: convertDateFormat(nextApp),
                          },
                        });
                      }}
                    >
                      Book
                    </Button>
                  </CardActions>
                </Card>
              ) : (
                <></>
              )}

              <div className="info_block w-100 d-flex justify-content-between align-self-end align-items-center">
                <div className="box_info_left">
                  <span className="desc mb-0">
                    {t("todaySection.HOME.health")}
                  </span>
                  <span className="txt_status d-block">
                    <HealthInfoStatusComponent
                      isFD={isFetchingHealthInfo}
                      isHIA={isHealthInfoAvailable}
                    />
                  </span>
                </div>
                <div className="btn_block">
                  <HealthInfoCTAComponent
                    isFD={isFetchingHealthInfo}
                    isHIA={isHealthInfoAvailable}
                  />
                </div>
              </div>
              {/* footer */}
              {/* <div style={subNoteStyle}>
                <p className="text-center desc w-100">
                  {t("todaySection.HOME.working1")}
                  <br />
                  {t("todaySection.HOME.working2")}
                  <br />
                  {t("todaySection.HOME.working3")}
                  <br />
                  {t("todaySection.HOME.working4")}
                  <br /> <br />
                  {t("todaySection.HOME.working5")}
                  <br />
                  {t("todaySection.HOME.working6")}
                  <br />
                  {t("todaySection.HOME.working7")}
                </p>
              </div> */}
            </>
          )}
          <BottomNav option="today" isChatButtonEnabled={isChatButtonEnabled} />
        </div>
      </div>
    </>
  );
}

export default HomePage;
